export type TLingoTileResult = 'CORRECT' | 'PRESENT' | 'ABSENT';

/**
 * Scores a guess against the answer, klank by klank (e.g. `['k', 'oe', 'k', 'j', 'e']`), the way Lingo does:
 * - `CORRECT`: the klank is in the right position
 * - `PRESENT`: the klank occurs in the answer, but somewhere else
 * - `ABSENT`: the klank doesn't occur in the answer (or all its occurrences are already accounted for)
 *
 * Duplicates are only marked `PRESENT` as often as they still occur in the answer after the `CORRECT` ones are taken out.
 */
export function evaluateLingoGuess(guess: readonly string[], answer: readonly string[]): TLingoTileResult[] {
  const results: TLingoTileResult[] = guess.map(() => 'ABSENT');
  const remaining = new Map<string, number>();

  answer.forEach((klank, i) => {
    if (guess[i] === klank) {
      results[i] = 'CORRECT';
    } else {
      remaining.set(klank, (remaining.get(klank) ?? 0) + 1);
    }
  });

  guess.forEach((klank, i) => {
    if (results[i] === 'CORRECT') return;

    const count = remaining.get(klank) ?? 0;
    if (count > 0) {
      results[i] = 'PRESENT';
      remaining.set(klank, count - 1);
    }
  });

  return results;
}

const RESULT_RANK: Record<TLingoTileResult, number> = { ABSENT: 0, PRESENT: 1, CORRECT: 2 };

/**
 * What the player knows about each klank they've tried so far, for colouring the keyboard: the best result the klank got in any guess.
 * A klank is only `ABSENT` when it never scored better, i.e. it doesn't occur in the answer at all.
 */
export function getLingoKeyStates(guesses: readonly { klanken: readonly string[]; results: readonly TLingoTileResult[] }[]) {
  const states = new Map<string, TLingoTileResult>();

  for (const { klanken, results } of guesses) {
    results.forEach((result, i) => {
      const current = states.get(klanken[i]);
      if (current === undefined || RESULT_RANK[result] > RESULT_RANK[current]) {
        states.set(klanken[i], result);
      }
    });
  }

  return states;
}
